// One conversation as the user lived it: the transcript turn by turn (each tool call with its outcome), the user's
// ratings pinned to the turns they name, their reports, the automatic signals, the model review's verdict, and the issues
// linked to it or to any of that evidence — each piece with "new issue" and "link to issue". Opening this page files an
// audit row on the API side (`admin.conversation_viewed`). A restricted conversation (out of the review's sample and
// outside its guardrail) stays closed until it is opened for a support request, which that audit row records.
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ApiError, api } from '../../services/api';
import { errorMessage, useAsync } from '../../hooks/useAsync';
import { dateTime, usd } from '../../services/format';
import { Alert, Badge, Button, Card, KeyValue, PageTitle } from '../../components/ui';
import type { ConversationDetail as Detail, ConversationEventEntry, ConversationReportEntry, FeedbackEntry, IssueLinkIn, IssueRef, ReplayGolden, ReplayOut, TranscriptMessage, TurnRecord } from '../../types/api';
import { IssueActions, IssueChips } from '../issues/common';
import { OutcomeBadge, RatingBadge, SeverityBadge, useLabel } from './common';
import { AgentMessage, turns } from './transcript';

/** The conversation, or no detail when the API keeps it closed (403 `restricted_conversation`) without a support request. */
async function load(id: string, support: boolean): Promise<{ detail: Detail | null }> {
  try {
    return { detail: await api.conversation(id, support) };
  } catch (err) {
    if (err instanceof ApiError && err.detail === 'restricted_conversation') return { detail: null };
    throw err;
  }
}

/** What a restricted conversation shows before it is opened: why it is closed, and the support request that opens it. */
function Restricted({ onOpen }: { onOpen: () => void }) {
  const { t } = useTranslation();
  return (
    <div>
      <PageTitle title={t('quality.restrictedTitle')} />
      <Card>
        <div className="flex flex-col gap-3" data-testid="restricted">
          <p className="text-[13px] text-ink">{t('quality.restrictedText')}</p>
          <div>
            <Button variant="primary" onClick={onOpen}>{t('quality.openForSupport')}</Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

function FeedbackLine({ f }: { f: FeedbackEntry }) {
  const { t } = useTranslation();
  const tag = useLabel('tag');
  if (f.outcome === 'dismissed') return <p className="text-[12px] text-faint">{t('quality.cardDismissed')}</p>;
  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap items-center gap-1">
        <RatingBadge rating={f.rating} />
        {f.tags.map((x) => (
          <Badge key={x}>{tag(x)}</Badge>
        ))}
      </div>
      {f.comment && <p className="text-[12.5px] italic text-ink">“{f.comment}”</p>}
    </div>
  );
}

/** A piece of evidence's issues as chips, and the actions that link it to one. */
function EvidenceIssues({ ids, issues, link, title, onLinked }: { ids: number[]; issues: IssueRef[]; link: IssueLinkIn; title: string; onLinked: (issue: IssueRef) => void }) {
  const mine = issues.filter((i) => ids.includes(i.id));
  return (
    <div className="flex flex-col gap-1.5">
      <IssueChips issues={mine} />
      <IssueActions link={link} title={title} onLinked={onLinked} />
    </div>
  );
}

/** The users' reports of the conversation, newest first, each with the issue it opened or joined. */
function ReportsCard({ reports, issues, idsOf, title, onLinked }: { reports: ConversationReportEntry[]; issues: IssueRef[]; idsOf: (key: string, base?: number[]) => number[]; title: string; onLinked: (key: string) => (issue: IssueRef) => void }) {
  const { t, i18n } = useTranslation();
  const reason = useLabel('reportReason');
  return (
    <Card title={t('quality.reports')}>
      <div className="flex flex-col gap-3" data-testid="reports">
        {reports.map((r) => (
          <div key={r.id} className="flex flex-col gap-1.5 border-b border-line-soft pb-3 last:border-0 last:pb-0" data-testid={`report-${r.id}`}>
            <div className="flex flex-wrap items-center gap-1">
              <Badge tone="bad">{reason(r.reason)}</Badge>
              <span className="font-mono text-[11px] text-faint">{dateTime(r.created_at, i18n.language)}</span>
            </div>
            {r.comment && <p className="text-[12.5px] italic text-ink">“{r.comment}”</p>}
            <EvidenceIssues ids={idsOf(`report:${r.id}`, r.issue_ids)} issues={issues} link={{ kind: 'report', ref: String(r.id) }}
                    title={`${t('issues.reportTitle')}: ${reason(r.reason)} — ${title}`} onLinked={onLinked(`report:${r.id}`)} />
          </div>
        ))}
      </div>
    </Card>
  );
}

/** "Sandbox from here" on a user's message: one click opens a sandbox from that turn and goes to it. */
function SandboxFromHere({ why, busy, onClick, index }: { why: string | null; busy: boolean; onClick: () => void; index: number }) {
  const { t } = useTranslation();
  return (
    <button type="button" disabled={!!why || busy} onClick={onClick} title={why ?? t('sandbox.fromHereHelp')} data-testid={`sandbox-from-${index}`}
            className="inline-flex shrink-0 items-center gap-1 rounded-sm px-1.5 py-0.5 font-mono text-[10.5px] text-accent-dark hover:bg-white disabled:cursor-default disabled:text-fainter disabled:hover:bg-transparent">
      {busy ? <span className="inline-block h-2.5 w-2.5 animate-pc-spin rounded-full border-[1.5px] border-accent border-t-transparent" /> : <span aria-hidden="true">▶</span>}
      {t('sandbox.fromHere')}
    </button>
  );
}

/** What a turn ran on: its index version, the release, the model and its effort, the mode. */
function ranOn(record: TurnRecord, t: (key: string, options?: Record<string, unknown>) => string): string {
  return [
    record.index_version !== null ? t('quality.turnIndex', { version: record.index_version }) : t('quality.turnNoIndex'),
    record.release,
    [record.model, record.effort].filter(Boolean).join(' '),
    record.mode,
  ].filter(Boolean).join(' · ');
}

/** Why a turn cannot be opened in a sandbox, or null when it can: its index version has to be stored. */
function notSandboxable(record: TurnRecord | undefined, t: (key: string) => string): string | null {
  if (!record || record.index_version === null) return t('sandbox.notRecorded');
  return record.stored ? null : t('quality.indexGone');
}

function Turn({ index, messages, feedback, events, worst, open, record, onSandbox, opening }: { index: number; messages: TranscriptMessage[]; feedback: FeedbackEntry[]; events: ConversationEventEntry[]; worst: boolean; open: boolean; record?: TurnRecord; onSandbox: () => void; opening: boolean }) {
  const { t } = useTranslation();
  const signal = useLabel('signal');
  return (
    <section className={`rounded-md border ${worst ? 'border-danger' : 'border-line-card'} bg-white`} data-testid={`turn-${index}`}>
      <header className="flex items-center justify-between gap-2 border-b border-line-soft px-4 py-2">
        <span className="font-mono text-[10.5px] uppercase tracking-[0.06em] text-faint">
          {t('quality.turn', { index })}
          {record && <span className="ml-2 normal-case tracking-normal text-fainter" data-testid={`turn-ran-on-${index}`}>{ranOn(record, t)}</span>}
        </span>
        <div className="flex flex-wrap gap-1">
          {record && record.index_version !== null && !record.stored && <Badge tone="warn">{t('quality.indexGone')}</Badge>}
          {worst && <Badge tone="bad">{t('quality.worstTurn')}</Badge>}
          {events.map((e, i) => (
            <Badge key={i} tone="warn">{signal(e.kind)}</Badge>
          ))}
        </div>
      </header>
      <div className="flex flex-col gap-3 p-4">
        {messages.map((m, i) =>
          m.role === 'user' ? (
            <div key={i} className="rounded-md bg-accent-bg px-3 py-2 text-[13px] text-ink">
              <div className="mb-0.5 flex items-center justify-between gap-2">
                <span className="font-mono text-[10.5px] uppercase text-accent-dark">{t('quality.userSaid')}</span>
                <SandboxFromHere why={notSandboxable(record, t)} busy={opening} onClick={onSandbox} index={index} />
              </div>
              <p className="whitespace-pre-wrap wrap-break-word">{m.content}</p>
            </div>
          ) : (
            <AgentMessage key={i} m={m} open={open} />
          ),
        )}
        {feedback.map((f, i) => (
          <div key={i} className="border-t border-line-soft pt-2">
            <FeedbackLine f={f} />
          </div>
        ))}
      </div>
    </section>
  );
}

export function ConversationDetail() {
  const { id = '' } = useParams();
  const { t, i18n } = useTranslation();
  // Opened for a support request: only a restricted conversation needs it, and the API records it in the audit row.
  // Held per conversation, so moving to another one never carries it over.
  const [supportFor, setSupportFor] = useState<string | null>(null);
  const support = supportFor === id;
  const { data: loaded, error } = useAsync(() => load(id, support), [id, support]);
  const data = loaded?.detail ?? null;
  // Every tool call, plan and reasoning folded or open at once; the key remounts the <details> so `open` applies.
  const [expanded, setExpanded] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [replay, setReplay] = useState<ReplayOut | null>(null);
  // The turn a sandbox is being opened from (one click on a message, or the header's button), and why it could not be.
  const [opening, setOpening] = useState<number | null>(null);
  const [sandboxError, setSandboxError] = useState<string | null>(null);
  const navigate = useNavigate();
  const openSandbox = async (turn: number) => {
    setOpening(turn);
    setSandboxError(null);
    try {
      const s = await api.openSandbox(id, { from_turn: turn });
      navigate(`/sandboxes/${s.id}`);
    } catch (err) {
      setSandboxError(errorMessage(err));
      setOpening(null);
    }
  };
  const [replaying, setReplaying] = useState(false);
  const [replayError, setReplayError] = useState<string | null>(null);
  // The reference accepted for this conversation: from the page's data, then from what the user accepts or retires here.
  const [golden, setGolden] = useState<ReplayGolden | null | undefined>(undefined);
  const [acceptReason, setAcceptReason] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);
  // Issues opened or linked from this page, added to what it loaded (no reload: each reading is an audited one).
  const [added, setAdded] = useState<{ issues: IssueRef[]; under: Record<string, number[]> }>({ issues: [], under: {} });
  useEffect(() => setAdded({ issues: [], under: {} }), [id]);
  const onLinked = (key: string) => (issue: IssueRef) =>
    setAdded((cur) => ({
      issues: [...cur.issues.filter((i) => i.id !== issue.id), issue],
      under: { ...cur.under, [key]: [...(cur.under[key] ?? []), issue.id] },
    }));
  const idsOf = (key: string, base: number[] = []) => [...new Set([...base, ...(added.under[key] ?? [])])];

  /** Freeze what the code produces now as the reference — the moment this conversation becomes an eval. */
  const accept = async () => {
    setAccepting(true);
    setReplayError(null);
    try {
      const g = await api.acceptReplay(id, acceptReason);
      setGolden(g);
      setReplay(null);
    } catch (err) {
      setReplayError(errorMessage(err));
    } finally {
      setAccepting(false);
    }
  };

  const retire = async () => {
    setAccepting(true);
    setReplayError(null);
    try {
      await api.retireReplay(id);
      setGolden(null);
      setReplay(null);
    } catch (err) {
      setReplayError(errorMessage(err));
    } finally {
      setAccepting(false);
    }
  };

  /** Play the conversation's server side again on the code running now; the API answers one verdict (audited). */
  const runReplay = async () => {
    setReplaying(true);
    setReplayError(null);
    try {
      setReplay(await api.replayConversation(id));
    } catch (err) {
      setReplayError(errorMessage(err));
    } finally {
      setReplaying(false);
    }
  };

  /** Save `conversation-<id>.json`: everything this page shows, as the API exports it (an audited export). */
  const download = async () => {
    setDownloading(true);
    setDownloadError(null);
    try {
      const blob = await api.downloadConversation(id, support);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `conversation-${id}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setDownloadError(errorMessage(err));
    } finally {
      setDownloading(false);
    }
  };
  const reason = useLabel('reason');
  const misuseKind = useLabel('misuseKind');
  const category = useLabel('category');
  const signal = useLabel('signal');

  if (error) return <Alert>{error}</Alert>;
  if (loaded && !loaded.detail) return <Restricted onOpen={() => setSupportFor(id)} />;
  if (!data) return <p className="text-faint">{t('common.loading')}</p>;
  const d: Detail = data;
  const c = d.conversation;
  const firstPlayable = (d.turns ?? []).find((r) => r.stored)?.turn_index;
  const review = d.reviews.find((r) => r.status === 'done') ?? null;
  const reference: ReplayGolden | null = golden === undefined ? (d.replay_golden ?? null) : golden;
  const reasonDraft = acceptReason ?? review?.eval_note ?? '';
  const card = d.feedback.find((f) => f.turn_index === null) ?? null;
  const byTurn = turns(d.messages);
  const negative = d.events.filter((e) => !['turn_started', 'turn_done'].includes(e.kind));
  const issues = [...(d.issues ?? []).filter((i) => !added.issues.some((a) => a.id === i.id)), ...added.issues];
  const titled = c.title || t('quality.untitled');

  return (
    <div>
      <PageTitle
        title={
          <>
            {c.title || t('quality.untitled')} <OutcomeBadge outcome={c.outcome} /> <SeverityBadge severity={c.severity} />
          </>
        }
        subtitle={
          <span className="font-mono">
            <Link className="underline" to={`/users/${c.user_id}`}>{c.user_email}</Link>
            {c.organization_id && (
              <>
                {' · '}
                <Link className="underline" to={`/organizations/${c.organization_id}`}>{c.organization_name}</Link>
              </>
            )}
            {' · '}
            {c.thread_id}
          </span>
        }
        actions={
          <>
            <Button onClick={() => setExpanded((v) => !v)} data-testid="expand-all">
              {expanded ? t('quality.collapseAll') : t('quality.expandAll')}
            </Button>
            <Button onClick={() => firstPlayable !== undefined && void openSandbox(firstPlayable)} disabled={firstPlayable === undefined}
                    busy={opening !== null && opening === firstPlayable} data-testid="sandbox-thread"
                    title={firstPlayable === undefined ? t('sandbox.nothingPlayable') : t('sandbox.openHelp')}>
              {t('sandbox.openButton')}
            </Button>
            <Button busy={replaying} onClick={() => void runReplay()} data-testid="replay-thread" title={t('quality.replayHelp')}>
              {t('quality.replay')}
            </Button>
            <Button variant="primary" busy={downloading} onClick={() => void download()} data-testid="download-thread">
              {t('quality.downloadThread')}
            </Button>
          </>
        }
      />
      <div className="mb-3 flex flex-col gap-1.5 rounded-md border border-line-card bg-white p-3" data-testid="conversation-issues">
        <div className="font-mono text-[10.5px] uppercase tracking-[0.06em] text-faint">{t('issues.title')}</div>
        <IssueChips issues={issues} />
        {!issues.length && <p className="text-[12.5px] text-faint">{t('issues.noneOnConversation')}</p>}
        <IssueActions link={{ kind: 'conversation', ref: c.thread_id }} title={titled} onLinked={onLinked(`conversation:${c.thread_id}`)} />
      </div>
      {downloadError && <div className="mb-3"><Alert>{downloadError}</Alert></div>}
      {sandboxError && <div className="mb-3"><Alert>{sandboxError}</Alert></div>}
      {replayError && <div className="mb-3"><Alert>{replayError}</Alert></div>}
      {replay && (
        <div className="mb-3" data-testid="replay-result">
          <Alert tone={replay.outcome === 'no_deviation' ? 'good' : replay.outcome === 'deviation' ? 'bad' : replay.outcome === 'not_replayable' ? 'warn' : 'neutral'}>
            <strong>{t('quality.replayTitle')}.</strong> {replay.summary}
            {(replay.turns ?? []).filter((x) => x.catalog !== 'as_run').map((x) => (
              <p key={x.turn} className="mt-1 text-[12px]" data-testid={`replay-turn-${x.turn}`}>
                {t(x.catalog === 'rebuilt' ? 'quality.replayRebuilt' : 'quality.replayMissing', { turn: x.turn, version: x.index_version })}
              </p>
            ))}
            {replay.deviations.length > 0 && (
              <ul className="mt-1.5 list-disc pl-4 font-mono text-[11.5px]">
                {replay.deviations.map((x, i) => (
                  <li key={i}>
                    {t('quality.turn', { index: x.turn })} · {x.tool}: {x.recorded} → {x.replayed}{x.note ? ` — ${x.note}` : ''}
                  </li>
                ))}
              </ul>
            )}
            {(replay.outcome === 'no_deviation' || replay.outcome === 'deviation') && (
              <div className="mt-2 flex flex-col gap-1.5 border-t border-line-soft pt-2" data-testid="accept-replay">
                <p className="text-[12px]">{t('quality.acceptHelp')}</p>
                <textarea value={reasonDraft} onChange={(e) => setAcceptReason(e.target.value)} rows={2} placeholder={t('quality.acceptReasonPlaceholder')}
                          className="w-full rounded-sm border border-line-card px-2 py-1 font-sans text-[12px] text-ink" data-testid="accept-reason" />
                <div>
                  <Button variant="primary" busy={accepting} onClick={() => void accept()} data-testid="accept-replay-button">
                    {reference ? t('quality.acceptAgain') : t('quality.accept')}
                  </Button>
                </div>
              </div>
            )}
          </Alert>
        </div>
      )}
      {reference && (
        <div className="mb-3" data-testid="replay-golden">
          <Alert tone="accent">
            <strong>{t('quality.goldenTitle')}.</strong>{' '}
            {t('quality.goldenLine', { when: dateTime(reference.accepted_at, i18n.language), who: reference.accepted_by_email ?? '—', version: reference.code_version ?? '—', calls: reference.compared })}
            {reference.reason && <p className="mt-1 italic">“{reference.reason}”</p>}
            <p className="mt-1 font-mono text-[11.5px]">
              {t('quality.goldenLastRun', { outcome: reference.last_outcome ?? '—', when: reference.last_replayed_at ? dateTime(reference.last_replayed_at, i18n.language) : '—', version: reference.last_code_version ?? '—' })}
            </p>
            <div className="mt-1.5">
              <Button variant="ghost" busy={accepting} onClick={() => void retire()} data-testid="retire-replay">
                {t('quality.retire')}
              </Button>
            </div>
          </Alert>
        </div>
      )}
      {/* Side column of a fixed width, transcript takes the rest; `minmax(0,…)` and `min-w-0` keep a wide tool call
          (a formula grid, a JSON result) inside its own scroller instead of stretching the tracks. */}
      <div className="grid gap-4 xl:grid-cols-[minmax(0,24rem)_minmax(0,1fr)]">
        <div className="flex min-w-0 flex-col gap-4">
          {!!d.reports?.length && <ReportsCard reports={d.reports} issues={issues} idsOf={idsOf} title={titled} onLinked={onLinked} />}
          <Card title={t('quality.verdict')}>
            {review ? (
              <div className="flex flex-col gap-3 text-[12.5px]">
                <p className="text-ink">{review.summary}</p>
                <KeyValue
                  rows={[
                    [t('quality.intent'), review.intent ?? '—'],
                    [t('quality.categoryLabel'), review.category ? category(review.category) : '—'],
                    [t('quality.outcomeLabel'), <OutcomeBadge key="o" outcome={review.outcome} />],
                    [t('quality.severityLabel'), review.severity ?? '—'],
                    [t('quality.sentiment'), review.sentiment ?? '—'],
                    [t('quality.failureReasons'), review.failure_reasons.length ? review.failure_reasons.map(reason).join(', ') : '—'],
                    [t('quality.misuse'), review.misuse.length ? review.misuse.map(misuseKind).join(', ') : '—'],
                    [t('quality.selected'), review.selected === 'flagged' ? `${t('quality.selectedFlagged')} (${review.flags.map(signal).join(', ')})` : t('quality.selectedSample')],
                    [t('quality.reviewedAt'), `${dateTime(review.reviewed_at, i18n.language)} · ${review.model ?? ''} · ${usd(review.cost_usd, i18n.language)}`],
                  ]}
                />
                {review.eval_candidate && (
                  <Alert tone="good">
                    <strong>{t('quality.evalCandidate')}.</strong> {review.eval_note}
                  </Alert>
                )}
                {review.product_insight && (
                  <div>
                    <div className="font-mono text-[11px] uppercase tracking-[0.06em] text-faint">{t('quality.productInsights')}</div>
                    <p className="mt-1 text-ink">{review.product_insight}</p>
                  </div>
                )}
                <EvidenceIssues ids={idsOf(`review:${review.id}`, review.issue_ids)} issues={issues}
                        link={{ kind: 'review', ref: String(review.id) }} title={review.summary?.slice(0, 200) || titled} onLinked={onLinked(`review:${review.id}`)} />
              </div>
            ) : (
              <p className="text-[12.5px] text-faint">{d.reviews.some((r) => r.status === 'failed') ? t('quality.reviewFailed') : t('quality.notReviewed')}</p>
            )}
          </Card>
          <Card title={t('quality.userRating')}>
            {card ? <FeedbackLine f={card} /> : <p className="text-[12.5px] text-faint">{t('quality.noCardRating')}</p>}
          </Card>
          {!!negative.length && (
            <Card title={t('quality.signals')}>
              <ul className="flex flex-col gap-3" data-testid="signals">
                {negative.map((e) => (
                  <li key={e.id} className="flex flex-col gap-1 border-b border-line-soft pb-2 last:border-0 last:pb-0" data-testid={`signal-${e.id}`}>
                    <div className="flex flex-wrap items-center gap-1">
                      <Badge tone="warn">{signal(e.kind)}</Badge>
                      <span className="font-mono text-[11px] text-faint">
                        {e.turn_index !== null ? `${t('quality.turn', { index: e.turn_index })} · ` : ''}{dateTime(e.created_at, i18n.language)}
                      </span>
                    </div>
                    <EvidenceIssues ids={idsOf(`signal:${e.id}`, e.issue_ids)} issues={issues} link={{ kind: 'signal', ref: String(e.id) }}
                            title={`${signal(e.kind)} — ${titled}`} onLinked={onLinked(`signal:${e.id}`)} />
                  </li>
                ))}
              </ul>
            </Card>
          )}
          <Card title={t('quality.facts')}>
            <KeyValue
              rows={[
                [t('quality.mode'), d.mode],
                [t('quality.turns'), c.turns],
                [t('quality.workbook'), d.workbook_id ?? '—'],
                [t('quality.started'), dateTime(c.created_at, i18n.language)],
                [t('quality.lastActivity'), dateTime(c.updated_at, i18n.language)],
                ...Object.entries(d.cost_by_purpose).map(([p, v]) => [t(`quality.costOf.${p}`, p), usd(v, i18n.language)] as [string, string]),
                [t('quality.signals'), negative.length ? negative.map((e) => signal(e.kind)).join(', ') : '—'],
              ]}
            />
          </Card>
        </div>
        <div className="flex min-w-0 flex-col gap-3">
          {[...byTurn.entries()].map(([index, messages]) => (
            <Turn key={`${index}-${expanded}`} index={index} messages={messages} worst={review?.worst_turn === index} open={expanded}
                  record={(d.turns ?? []).find((r) => r.turn_index === index)}
                  onSandbox={() => void openSandbox(index)} opening={opening === index}
                  feedback={d.feedback.filter((f) => f.turn_index === index)} events={negative.filter((e) => e.turn_index === index)} />
          ))}
          {!byTurn.size && <p className="text-faint">{t('quality.noMessages')}</p>}
        </div>
      </div>
    </div>
  );
}
